import {
  ISearchBoxStyleProps,
  ISearchBoxStyles
} from '@fluentui/react/lib/SearchBox';
import { IPalette, IStyle, ITheme } from '@fluentui/react/lib/Styling';
import { ContrastColors } from '../teams-colors';
import { teamsBorderRadius, teamsButtonBorderRadius } from './constants';

interface IName {
  FIELD_GROUP_BACKGROUND_COLOR: string;
  FIELD_GROUP_OPACITY: number;
  FIELD_COLOR_PLACEHOLDER_NOT_DISABLED: string;
  FIELD_COLOR_PLACEHOLDER_DISABLED: string;
  FIELD_GROUP_BORDER_WIDTH: string;
  FIELD_GROUP_BORDER_COLOR: string;
  FIELD_GROUP_BORDER_COLOR_DISABLED: string;
  FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE: string;
  FIELD_GROUP_BORDER_WIDTH_ACTIVE: string;
}

const getFieldStyle = (
  palette: IPalette,
  themeName: string,
  background?: string
): IName => {
  const fieldStyle: IName = {
    FIELD_GROUP_BACKGROUND_COLOR: palette.neutralLight,
    FIELD_GROUP_OPACITY: 0.36,
    FIELD_COLOR_PLACEHOLDER_NOT_DISABLED: palette.neutralDark,
    FIELD_COLOR_PLACEHOLDER_DISABLED: palette.neutralDark,
    FIELD_GROUP_BORDER_WIDTH: '0 0 2px 0',
    FIELD_GROUP_BORDER_COLOR: 'transparent',
    FIELD_GROUP_BORDER_COLOR_DISABLED: 'transparent',
    FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE: palette.themePrimary,
    FIELD_GROUP_BORDER_WIDTH_ACTIVE: '0 0 2px 0'
  };

  // Dark theme overrides
  if (themeName === 'dark') {
    fieldStyle.FIELD_GROUP_BACKGROUND_COLOR = palette.white;
    fieldStyle.FIELD_COLOR_PLACEHOLDER_DISABLED = palette.neutralPrimary;
  }

  // Contrast theme overrides
  if (themeName === 'contrast') {
    fieldStyle.FIELD_GROUP_BACKGROUND_COLOR = palette.white;
    fieldStyle.FIELD_GROUP_OPACITY = 1;
    fieldStyle.FIELD_COLOR_PLACEHOLDER_NOT_DISABLED = palette.black;
    fieldStyle.FIELD_COLOR_PLACEHOLDER_DISABLED =
      ContrastColors.appGreenContrast;
    fieldStyle.FIELD_GROUP_BORDER_WIDTH = '1px 1px 1px 1px';
    fieldStyle.FIELD_GROUP_BORDER_COLOR = palette.black;
    fieldStyle.FIELD_GROUP_BORDER_COLOR_DISABLED =
      ContrastColors.appGreenContrast;
    fieldStyle.FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE = palette.themeDark;
    fieldStyle.FIELD_GROUP_BORDER_WIDTH_ACTIVE = '1px 1px 2px 1px';
  }
  // If this textfiled is on gray background
  if (background === 'gray') {
    fieldStyle.FIELD_GROUP_BACKGROUND_COLOR = palette.white;
    fieldStyle.FIELD_COLOR_PLACEHOLDER_NOT_DISABLED = palette.neutralPrimaryAlt;
  }

  return fieldStyle;
};

const getClearButton = (fieldStyle: IName): IStyle => ({
  selectors: {
    '.ms-Button': {
      color: fieldStyle.FIELD_COLOR_PLACEHOLDER_NOT_DISABLED,
      selectors: {
        ':hover': {
          color: fieldStyle.FIELD_COLOR_PLACEHOLDER_NOT_DISABLED
        },
        ':active': {
          color: fieldStyle.FIELD_COLOR_PLACEHOLDER_NOT_DISABLED
        }
      }
    }
  }
});

const getHasFocus = (fieldStyle: IName): IStyle => ({
  borderBottomColor: fieldStyle.FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE,
  borderWidth: fieldStyle.FIELD_GROUP_BORDER_WIDTH_ACTIVE,
  // Need 2px border raidus as the border width is 2px.
  borderBottomRightRadius: teamsButtonBorderRadius,
  borderBottomLeftRadius: teamsButtonBorderRadius,
  selectors: {
    ':hover': {
      borderBottomColor: fieldStyle.FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE,
      borderWidth: fieldStyle.FIELD_GROUP_BORDER_WIDTH_ACTIVE,
      // Need 2px border raidus as the border width is 2px.
      borderBottomRightRadius: teamsButtonBorderRadius,
      borderBottomLeftRadius: teamsButtonBorderRadius
    }
  }
});

const getRoot = (
  fieldStyle: IName,
  disabled?: boolean,
  hasFocus?: boolean
): IStyle => [
  {
    background: fieldStyle.FIELD_GROUP_BACKGROUND_COLOR,
    borderRadius: teamsBorderRadius,
    borderWidth: fieldStyle.FIELD_GROUP_BORDER_WIDTH,
    borderStyle: 'Solid',
    borderColor: fieldStyle.FIELD_GROUP_BORDER_COLOR,
    height: 32,
    paddingLeft: 12,

    selectors: {
      ':hover': {
        borderColor: fieldStyle.FIELD_GROUP_BORDER_COLOR
      },
      '.ms-SearchBox-field': {
        selectors: {
          '::placeholder': [
            !disabled && {
              color: fieldStyle.FIELD_COLOR_PLACEHOLDER_NOT_DISABLED
            },
            disabled && { color: fieldStyle.FIELD_COLOR_PLACEHOLDER_DISABLED }
          ],
          ':-ms-input-placeholder': [
            !disabled && {
              color: fieldStyle.FIELD_COLOR_PLACEHOLDER_NOT_DISABLED
            },
            disabled && { color: fieldStyle.FIELD_COLOR_PLACEHOLDER_DISABLED }
          ]
        }
      }
    }
  },
  disabled && {
    opacity: fieldStyle.FIELD_GROUP_OPACITY,
    borderColor: fieldStyle.FIELD_GROUP_BORDER_COLOR_DISABLED
  },
  !hasFocus &&
    !disabled && {
      selectors: {
        ':hover': {
          borderColor: fieldStyle.FIELD_GROUP_BORDER_COLOR
        }
      }
    },
  hasFocus && getHasFocus(fieldStyle)
];

export function getSearchBoxStyles(
  themeName: string,
  _theme: ITheme,
  background?: string
) {
  return (props: ISearchBoxStyleProps): Partial<ISearchBoxStyles> => {
    const { theme, disabled, hasInput, hasFocus } = props;
    const { palette } = theme;
    const fieldStyle = getFieldStyle(palette, themeName, background);

    const styles = {
      root: getRoot(fieldStyle, disabled, hasFocus),
      field: {
        color: palette.neutralDark,
        height: 30,
        selectors: {}
      },
      iconContainer: [
        {
          transition: 'none',
          width: '32px',
          height: 30,
          paddingLeft: 1,
          paddingRight: 1,
          order: 2
        },
        hasInput && {
          display: 'none'
        }
      ],
      clearButton: getClearButton(fieldStyle),
      icon: [
        {
          opacity: 1,
          paddingLeft: 4,
          paddingRight: 4,
          transform: 'scaleX(-1)'
        },
        !disabled && { color: fieldStyle.FIELD_COLOR_PLACEHOLDER_NOT_DISABLED },
        disabled && { color: fieldStyle.FIELD_COLOR_PLACEHOLDER_DISABLED },

        hasInput && {
          display: 'none'
        }
      ]
    } as Partial<ISearchBoxStyles>;

    return styles;
  };
}
